/*
 * AAIKO quick shop.
 *
 * The drawn card (265:8509) puts a size strip over the bottom of the media.
 * A cursor opens it on hover, which is pure CSS; this file covers the two
 * paths CSS cannot do:
 *
 *   1. the bag glyph in Extras (242:8626) toggles the strip, which is how
 *      touch and keyboard reach it;
 *   2. a size adds that exact variant to the bag.
 *
 * Nothing about cart state is computed here. The POST goes to /cart/add.js
 * and the result is announced on `shopify:cart:lines-update` with
 * action 'add', which is the contract aaiko-cart-drawer.js documents: it
 * refreshes itself and opens.
 */
(function () {
  'use strict';

  /* Included by every surface that renders the card with a strip - the
     collection grid, the PDP, the favourites, the look grid - and, since
     03/09/2026, by the bag drawer on every page. A page can therefore carry
     this file twice, and the browser runs a repeated <script src> again: two
     click listeners would add every tapped size twice. Register once. */
  if (window.aaikoQuickshopReady) return;
  window.aaikoQuickshopReady = true;

  var CART_EVENT = 'shopify:cart:lines-update';

  function cartRoutes() {
    var fallback = { cart_add_url: '/cart/add.js', cart_url: '/cart' };
    try {
      /* snippets/scripts.liquid declares `const Theme` on the lexical scope,
         not on window, so this is a bare-identifier read behind a guard. */
      // eslint-disable-next-line no-undef
      return (typeof Theme !== 'undefined' && Theme.routes) || fallback;
    } catch (error) {
      return fallback;
    }
  }

  /* A host is the product card or, since 14/10/2026, any element marked
     [data-aaiko-quickshop-host] - the bag panel's recommendation rows, which
     are a list row rather than a card but drive the same toggle and add. */
  var HOST = '.aaiko-card, [data-aaiko-quickshop-host]';

  function card(el) {
    return el.closest(HOST);
  }

  function close(target) {
    if (!target) return;
    target.removeAttribute('data-quickshop-open');
    var toggle = target.querySelector('[data-aaiko-quickshop-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  function closeAll(except) {
    var open = document.querySelectorAll('.aaiko-card[data-quickshop-open], [data-aaiko-quickshop-host][data-quickshop-open]');
    for (var i = 0; i < open.length; i++) {
      if (open[i] !== except) close(open[i]);
    }
  }

  function onToggle(button) {
    var host = card(button);
    if (!host) return;
    var isOpen = host.hasAttribute('data-quickshop-open');
    closeAll(host);
    if (isOpen) {
      close(host);
      return;
    }
    host.setAttribute('data-quickshop-open', '');
    button.setAttribute('aria-expanded', 'true');
  }

  /* "Toegevoegd" in black, and no answer to a second tap, or a double tap would
     put two in the bag. */
  function showAdded(host, button) {
    host.setAttribute('data-quickshop-added', '');
    button.setAttribute('aria-disabled', 'true');
  }

  function clearAdded(host, button) {
    host.removeAttribute('data-quickshop-added');
    button.removeAttribute('aria-disabled');
  }

  function add(button) {
    var host = card(button);
    /* CLIENT REQUEST (Tessa van Huizen, 27/10/2026, 4.9): the PDP's Combineer
       met rows keep the shopper on the page, because the product they came for
       is usually not in the bag yet. Any action but 'add' refreshes the bag
       panel without opening it (aaiko-cart-drawer.js), and the row's own button
       says what happened. */
    var stay = host && host.hasAttribute('data-aaiko-quickshop-stay');
    var status = stay ? host.querySelector('[data-aaiko-quickshop-status]') : null;
    /* In such a row the size is a radio and the button adds what it holds. */
    var picked = stay ? host.querySelector('input[type="radio"]:checked') : null;
    var id = picked ? picked.value : button.getAttribute('data-variant-id');
    if (button.getAttribute('aria-busy') === 'true' || button.getAttribute('aria-disabled') === 'true') return;
    if (!id) {
      if (status) {
        status.setAttribute('data-error', '');
        status.textContent = status.getAttribute('data-pick-text') || '';
      }
      return;
    }

    var routes = cartRoutes();
    var resolve;
    var reject;
    var promise = new Promise(function (res, rej) {
      resolve = res;
      reject = rej;
    });

    /* Announced BEFORE the request settles, exactly as Horizon's own button
       does: the drawer waits on the promise and only then refreshes, so a
       slow network cannot leave it showing a stale bag. */
    var event = new CustomEvent(CART_EVENT, { bubbles: true, detail: { source: 'aaiko-quickshop' } });
    event.action = stay ? 'stay' : 'add';
    event.promise = promise;
    document.dispatchEvent(event);

    button.setAttribute('aria-busy', 'true');
    if (status) {
      status.removeAttribute('data-error');
      status.textContent = '';
    }

    /* CLIENT FEEDBACK (02/09/2026): "werkt niet duidelijk, de maat gaat te
       langzaam de winkelmand in". The strip used to stay open until the add
       AND the follow-up cart read both settled - two sequential round trips
       with the shopper staring at an unchanged row. The tapped cell inverts
       via [aria-busy] the instant the tap lands (card CSS), holds for a beat
       so the answer is seen, and the strip closes on its own; the drawer
       still opens only when the promise resolves, so nothing claims success
       before the cart says so. On failure the strip is already closed - the
       promise rejection reaches the drawer exactly as before. */
    window.setTimeout(function () {
      close(host);
    }, 250);

    var body = new FormData();
    body.append('id', id);
    body.append('quantity', '1');

    fetch(routes.cart_add_url, { method: 'POST', headers: { Accept: 'application/json' }, body: body })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data && data.status) throw new Error(data.description || data.message || 'add failed');
        return fetch(routes.cart_url + '.js').then(function (response) {
          return response.json();
        });
      })
      .then(function (cart) {
        resolve({ detail: { source: 'aaiko-quickshop', itemCount: cart.item_count, items: cart.items } });
        if (status) {
          status.textContent = (picked || button).getAttribute('data-added-text') || '';
          if (picked) picked.setAttribute('data-added', '');
          showAdded(host, button);
        }
      })
      .catch(function (error) {
        console.warn('[aaiko-quickshop] add failed', error);
        reject(error);
        if (status) {
          status.setAttribute('data-error', '');
          status.textContent = status.getAttribute('data-error-text') || '';
        }
      })
      .then(function () {
        button.removeAttribute('aria-busy');
      });
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-aaiko-quickshop-toggle]');
    if (toggle) {
      event.preventDefault();
      onToggle(toggle);
      return;
    }

    var size = event.target.closest('[data-aaiko-quickshop-add]');
    if (size) {
      event.preventDefault();
      add(size);
      return;
    }

    /* A click anywhere else closes whatever the bag glyph opened. Hover-opened
       strips are CSS-only and close themselves. */
    if (!event.target.closest('.aaiko-card[data-quickshop-open], [data-aaiko-quickshop-host][data-quickshop-open]')) closeAll(null);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeAll(null);
  });

  /* Another size in a stay row: the button answers for the size now chosen, so
     "Toegevoegd" comes back only on a size that went into the bag. */
  document.addEventListener('change', function (event) {
    var host = event.target.closest('[data-aaiko-quickshop-stay]');
    if (!host) return;
    var button = host.querySelector('[data-aaiko-quickshop-add]');
    var status = host.querySelector('[data-aaiko-quickshop-status]');
    if (status) {
      status.removeAttribute('data-error');
      status.textContent = '';
    }
    if (event.target.hasAttribute('data-added')) showAdded(host, button);
    else clearAdded(host, button);
  });

  /* A change in the bag panel can take out what a stay row just added, and that
     row would go on saying "Toegevoegd" with its size inert. Back to neutral:
     what the bag holds is the panel's to show. */
  document.addEventListener(CART_EVENT, function (event) {
    if (!event.detail || event.detail.source !== 'aaiko-cart-drawer') return;
    var rows = document.querySelectorAll('[data-aaiko-quickshop-stay][data-quickshop-added]');
    for (var i = 0; i < rows.length; i++) {
      clearAdded(rows[i], rows[i].querySelector('[data-aaiko-quickshop-add]'));
    }
    var added = document.querySelectorAll('[data-aaiko-quickshop-stay] [data-added]');
    for (var j = 0; j < added.length; j++) added[j].removeAttribute('data-added');
  });
})();
